import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Plus, Trash2, Search, Check, Truck } from "lucide-react";

const emptyRow = {
  waste_stream_id: "", waste_stream_name: "",
  waste_type: "recyclable", disposal_method: "recycling",
  treatment_id: "", treatment_name: "",
  unit: "kg", quantity: "", notes: "",
};

/**
 * Batch collection logging wizard.
 * Step 1: Client + Site + Date + Driver + Vehicle
 * Step 2: Add rows (waste stream + quantity + treatment). Add as many as needed.
 * Step 3: Review & submit.
 * If `initial.jobContext` is passed (pending pickup job) the first step is pre-filled and locked.
 */
export default function LogWizard({ onClose, onSaved, initial = null }) {
  const today = new Date().toISOString().slice(0, 10);
  const jobCtx = initial?.jobContext || null;

  const [step, setStep] = useState(1);
  const [header, setHeader] = useState({
    client_id: jobCtx?.client_id || "",
    site_id: jobCtx?.site_id || "",
    collection_date: jobCtx?.trip_date || today,
    driver: jobCtx?.driver || "",
    driver_user_id: "",
    vehicle: jobCtx?.vehicle || "",
    vehicle_id: "",
    trip_id: jobCtx?.trip_id || null,
    stop_index: jobCtx?.stop_index ?? null,
  });
  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [streams, setStreams] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rowSearch, setRowSearch] = useState({});

  useEffect(() => {
    api.get("/waste-catalog/streams").then((r) => setStreams(r.data)).catch(() => {});
    api.get("/waste-catalog/treatments").then((r) => setTreatments(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/drivers").then((r) => setDrivers(r.data.filter((d) => d.active && d.has_password))).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data.filter((v) => v.active))).catch(() => {});
  }, []);

  const treatmentsById = useMemo(() => Object.fromEntries(treatments.map((t) => [t.id, t])), [treatments]);
  const clientsById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const sitesForClient = (cid) => sites.filter((s) => s.client_id === cid);
  const clientName = clientsById[header.client_id]?.name || "—";

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const addRow = () => setRows((rs) => [...rs, { ...emptyRow }]);

  const pickStreamForRow = (i, s) => {
    const dt = s.default_treatment_id ? treatmentsById[s.default_treatment_id] : null;
    updateRow(i, {
      waste_stream_id: s.id,
      waste_stream_name: s.name,
      waste_type: s.waste_type,
      unit: s.unit,
      treatment_id: dt?.id || "",
      treatment_name: dt?.name || "",
      disposal_method: dt?.disposal_method || "recycling",
    });
    setRowSearch((r) => ({ ...r, [i]: "" }));
  };

  const pickTreatmentForRow = (i, tid) => {
    const t = treatmentsById[tid];
    if (!t) return;
    updateRow(i, { treatment_id: t.id, treatment_name: t.name, disposal_method: t.disposal_method });
  };

  const canGoStep2 = header.client_id && header.collection_date && header.driver && header.vehicle;
  const rowsValid = rows.length > 0 && rows.every((r) => r.waste_stream_id && r.treatment_id && Number(r.quantity) > 0);

  const streamGroups = (rowIdx) => {
    const q = (rowSearch[rowIdx] || "").toLowerCase();
    const filtered = streams.filter((s) => s.active !== false && (!q || s.name.toLowerCase().includes(q)));
    const g = {};
    filtered.forEach((s) => { g[s.category] = g[s.category] || []; g[s.category].push(s); });
    return g;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        client_id: header.client_id,
        site_id: header.site_id || null,
        collection_date: header.collection_date,
        driver: header.driver,
        vehicle: header.vehicle,
        trip_id: header.trip_id || null,
        stop_index: header.stop_index ?? null,
        rows: rows.map((r) => ({
          client_id: header.client_id,
          site_id: header.site_id || null,
          collection_date: header.collection_date,
          waste_type: r.waste_type,
          disposal_method: r.disposal_method,
          waste_stream_id: r.waste_stream_id,
          waste_stream_name: r.waste_stream_name,
          treatment_id: r.treatment_id,
          treatment_name: r.treatment_name,
          unit: r.unit,
          quantity: Number(r.quantity),
          weight_kg: Number(r.quantity),
          notes: r.notes || null,
        })),
      };
      const { data } = await api.post("/collections/batch", payload);
      toast.success(`${data.count} entries logged`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => !saving && onClose()}>
      <div className="hbt-card w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="log-wizard">
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="hbt-label">{jobCtx ? "Log pickup" : "Log collection"} · Step {step} of 3</div>
            <div className="font-display text-xl font-semibold mt-0.5">
              {step === 1 && "Pickup details"}
              {step === 2 && `Waste streams — ${clientName}`}
              {step === 3 && "Review & log"}
            </div>
          </div>
          <button onClick={onClose} data-testid="wizard-close"><X className="w-5 h-5 text-neutral-400" /></button>
        </div>

        {/* Step progress bar */}
        <div className="px-5 pt-4">
          <div className="flex gap-2">
            {[1,2,3].map((n) => (
              <div key={n} className="flex-1 h-1 rounded-full transition-colors" style={{ background: step >= n ? "var(--forest)" : "rgba(10,10,10,0.10)" }} />
            ))}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-4">
              {jobCtx && (
                <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: "rgba(26,54,45,0.06)" }}>
                  <Truck className="w-5 h-5" style={{ color: "var(--forest)" }} />
                  <div>
                    <div className="text-sm font-semibold">Pickup from driver&apos;s trip {jobCtx.trip_number}</div>
                    <div className="text-xs text-neutral-500">Client, date, driver and truck are pre-filled from the completed stop.</div>
                  </div>
                </div>
              )}
              <div>
                <label className="hbt-label block mb-2">Client</label>
                <select required className="hbt-input" value={header.client_id} disabled={!!jobCtx}
                  onChange={(e) => setHeader({ ...header, client_id: e.target.value, site_id: "" })} data-testid="wiz-client">
                  <option value="">Select…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Site (optional)</label>
                  <select className="hbt-input" value={header.site_id || ""} disabled={!!jobCtx}
                    onChange={(e) => setHeader({ ...header, site_id: e.target.value })} data-testid="wiz-site">
                    <option value="">— None —</option>
                    {sitesForClient(header.client_id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="hbt-label block mb-2">Date</label>
                  <input type="date" required className="hbt-input" value={header.collection_date} disabled={!!jobCtx}
                    onChange={(e) => setHeader({ ...header, collection_date: e.target.value })} data-testid="wiz-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Driver</label>
                  <select required className="hbt-input" value={header.driver_user_id || ""} disabled={!!jobCtx}
                    onChange={(e) => {
                      const d = drivers.find((x) => x.user_id === e.target.value);
                      const v = vehicles.find((x) => x.id === d?.assigned_vehicle_id);
                      setHeader({ ...header, driver_user_id: e.target.value, driver: d?.name || "",
                        vehicle_id: v?.id || header.vehicle_id, vehicle: v?.registration || header.vehicle });
                    }} data-testid="wiz-driver">
                    <option value="">Select…</option>
                    {drivers.map((d) => <option key={d.user_id} value={d.user_id}>{d.name}</option>)}
                    {jobCtx && !drivers.find((d) => d.name === header.driver) && <option value="">{header.driver}</option>}
                  </select>
                  {jobCtx && <div className="text-xs text-neutral-500 mt-1">Locked from trip: {header.driver}</div>}
                </div>
                <div>
                  <label className="hbt-label block mb-2">Vehicle</label>
                  <select required className="hbt-input" value={header.vehicle_id || ""} disabled={!!jobCtx}
                    onChange={(e) => {
                      const v = vehicles.find((x) => x.id === e.target.value);
                      setHeader({ ...header, vehicle_id: e.target.value, vehicle: v?.registration || "" });
                    }} data-testid="wiz-vehicle">
                    <option value="">Select…</option>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.registration}</option>)}
                    {jobCtx && !vehicles.find((v) => v.registration === header.vehicle) && <option value="">{header.vehicle}</option>}
                  </select>
                  {jobCtx && <div className="text-xs text-neutral-500 mt-1">Locked from trip: {header.vehicle}</div>}
                </div>
              </div>
              {jobCtx && (jobCtx.photo_before || jobCtx.photo_after) && (
                <div>
                  <div className="hbt-label mb-2">Site photos from driver</div>
                  <div className="grid grid-cols-2 gap-3">
                    {jobCtx.photo_before && <img src={jobCtx.photo_before} alt="before" className="w-full h-32 object-cover rounded border" data-testid="job-photo-before" />}
                    {jobCtx.photo_after && <img src={jobCtx.photo_after} alt="after" className="w-full h-32 object-cover rounded border" data-testid="job-photo-after" />}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-4">
              {rows.map((r, i) => (
                <div key={i} className="rounded-lg border p-4" style={{ borderColor: "var(--border)" }} data-testid={`wiz-row-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold">Stream #{i + 1}{r.waste_stream_name ? ` — ${r.waste_stream_name}` : ""}</div>
                    {rows.length > 1 && <button onClick={() => removeRow(i)} className="text-neutral-400 hover:text-red-700" data-testid={`wiz-remove-row-${i}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  {!r.waste_stream_id ? (
                    <>
                      <div className="relative mb-2">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <input placeholder="Search streams…" className="hbt-input pl-9 text-sm"
                          value={rowSearch[i] || ""} onChange={(e) => setRowSearch({ ...rowSearch, [i]: e.target.value })}
                          data-testid={`wiz-stream-search-${i}`} />
                      </div>
                      <div className="max-h-40 overflow-y-auto border rounded p-1" style={{ borderColor: "var(--border)" }}>
                        {Object.entries(streamGroups(i)).map(([cat, list]) => (
                          <div key={cat}>
                            <div className="text-[10px] uppercase tracking-wider text-neutral-500 px-2 py-1">{cat.replace("_", " ")}</div>
                            <div className="grid grid-cols-2 gap-1">
                              {list.map((s) => (
                                <button key={s.id} type="button" onClick={() => pickStreamForRow(i, s)}
                                  className="text-left text-xs px-3 py-2 rounded border hover:bg-neutral-50 flex items-center justify-between gap-2"
                                  style={{ borderColor: "rgba(10,10,10,0.10)" }}
                                  data-testid={`wiz-stream-opt-${i}-${s.id}`}>
                                  <span className="truncate">{s.name}</span>
                                  <span className="text-[10px] font-mono text-neutral-500">{s.unit}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="hbt-label block mb-2">Quantity ({r.unit === "L" ? "L" : "kg"})</label>
                        <input type="number" min="0" step="0.1" required className="hbt-input"
                          value={r.quantity} onChange={(e) => updateRow(i, { quantity: e.target.value })}
                          data-testid={`wiz-qty-${i}`} />
                      </div>
                      <div>
                        <label className="hbt-label block mb-2">Treatment</label>
                        <select required className="hbt-input" value={r.treatment_id}
                          onChange={(e) => pickTreatmentForRow(i, e.target.value)}
                          data-testid={`wiz-treatment-${i}`}>
                          <option value="">Select…</option>
                          {treatments.filter((t) => t.active !== false).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <button type="button" onClick={() => updateRow(i, { waste_stream_id: "", waste_stream_name: "" })} className="text-xs underline text-neutral-500">Change stream</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" onClick={addRow} className="btn-outline w-full py-3 inline-flex items-center justify-center gap-2" data-testid="wiz-add-row">
                <Plus className="w-4 h-4" /> Add another stream for this pickup
              </button>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded-lg p-3 text-sm" style={{ background: "rgba(26,54,45,0.05)" }}>
                <div><span className="text-neutral-500">Client:</span> <span className="font-semibold">{clientName}</span></div>
                <div className="mt-1"><span className="text-neutral-500">Date:</span> <span className="font-mono-num">{header.collection_date}</span> · <span className="text-neutral-500">Driver:</span> {header.driver} · <span className="text-neutral-500">Truck:</span> {header.vehicle}</div>
              </div>
              <table className="hbt-table w-full">
                <thead><tr><th>Stream</th><th>Treatment</th><th className="num">Quantity</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-testid={`wiz-review-row-${i}`}>
                      <td>{r.waste_stream_name}</td>
                      <td className="text-neutral-600 text-sm">{r.treatment_name}</td>
                      <td className="num font-mono-num font-semibold">{Number(r.quantity || 0).toFixed(1)} {r.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-neutral-500">Once you tap <b>Log all</b>, the entries land in the client&apos;s waste log and their sustainability reports.</p>
            </div>
          )}
        </div>

        <div className="p-5 border-t flex items-center justify-between gap-2" style={{ borderColor: "var(--border)" }}>
          <button onClick={step === 1 ? onClose : () => setStep(step - 1)} className="btn-outline inline-flex items-center gap-1" disabled={saving} data-testid="wiz-back">
            {step === 1 ? "Cancel" : (<><ChevronLeft className="w-4 h-4" /> Back</>)}
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !canGoStep2 : !rowsValid}
              className="btn-primary inline-flex items-center gap-1"
              data-testid="wiz-next"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={saving || !rowsValid} className="btn-primary inline-flex items-center gap-2" data-testid="wiz-submit">
              <Check className="w-4 h-4" /> {saving ? "Logging…" : `Log all (${rows.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
